import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { motion } from "framer-motion";
import { MapPin, CalendarDays } from "lucide-react";
import ReviewForm from "./ReviewForm";

function MyReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReviewId, setOpenReviewId] = useState(null);
  const [editReviewId, setEditReviewId] = useState(null); // _id de l'avis en cours d'édition

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const res = await axios.get(
          "https://api.komorotel.com/api/reservation/user",
          { withCredentials: true }
        );
        setReservations(res.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReservations();
  }, []);

  const handleCancelRequest = async (id) => {
    if (!window.confirm("Es-tu sûr de vouloir annuler cette réservation ?")) return;
    try {
      await axios.post(
        `https://api.komorotel.com/api/reservation/${id}/cancellation-request`,
        { reservationId: id },
        { withCredentials: true }
      );
      setReservations((prev) =>
        prev.map((r) => r._id === id ? { ...r, status: "pending_cancel" } : r)
      );
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    }
  };

  // Soumettre un nouvel avis
  const handleReviewSuccess = (reservationId) => {
    setOpenReviewId(null);
    setReservations((prev) =>
      prev.map((r) =>
        r._id === reservationId ? { ...r, review: { submitted: true } } : r
      )
    );
  };

  // Modifier un avis existant
  const handleEditSuccess = (reservationId, { rating, comment }) => {
    setEditReviewId(null);
    setReservations((prev) =>
      prev.map((r) =>
        r._id === reservationId
          ? { ...r, review: { ...r.review, rating, comment } }
          : r
      )
    );
  };

  const getStatusUI = (status) => {
    switch (status) {
      case "confirmed":    return { text: "Confirmée",          color: "bg-green-500" };
      case "completed":    return { text: "Terminée",           color: "bg-gray-400" };
      case "pending_cancel": return { text: "Annulation en cours", color: "bg-yellow-500" };
      case "cancelled":    return { text: "Annulée",            color: "bg-red-500" };
      default:             return { text: "Inconnue",           color: "bg-gray-400" };
    }
  };

  return (
    <>
      <Header />
      <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-gray-900">Mes réservations</h1>
            <p className="text-gray-500 mt-1">Consultez et gérez vos séjours</p>
          </div>

          {loading && <p className="text-gray-500">Chargement...</p>}

          {!loading && reservations.length === 0 && (
            <div className="bg-white rounded-2xl p-8 text-center border">
              <p className="text-gray-500">Aucune réservation pour le moment.</p>
            </div>
          )}

          <div className="flex flex-col gap-6">
            {reservations.map((r, i) => {
              const status = getStatusUI(r.status);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const checkIn  = new Date(r.checkInDate);  checkIn.setHours(0, 0, 0, 0);
              const checkOut = new Date(r.checkOutDate); checkOut.setHours(0, 0, 0, 0);

              const isUpcoming = today < checkIn;
              const isOngoing  = today >= checkIn && today < checkOut;

              const hasPolicy = !!r.ratePlanSnapshot?.cancellationPolicy;
              const policy    = r.ratePlanSnapshot?.cancellationPolicy;
              const now       = new Date();
              const diffHours = (checkIn.getTime() - now.getTime()) / (1000 * 60 * 60);

              let refundText = "", policyText = "", policyColor = "text-gray-500", isRefundable = true;

              if (!hasPolicy) {
                policyText = "Conditions d'annulation à confirmer avec l'hôtel";
                refundText = "Remboursement selon validation de l'hôtel";
              }
              if (hasPolicy && policy.type === "free") {
                const isFree = diffHours >= policy.hoursBeforeArrival;
                policyText   = `Annulation gratuite jusqu'à ${policy.hoursBeforeArrival}h avant l'arrivée`;
                refundText   = isFree ? "Remboursement total si annulation maintenant" : "Non remboursable à cette date";
                policyColor  = isFree ? "text-green-600" : "text-red-500";
              }
              if (hasPolicy && policy.type === "partial") {
                policyText  = `Gratuit jusqu'à ${policy.hoursBeforeArrival}h avant l'arrivée puis ${policy.penaltyPercentage}% de frais`;
                refundText  = diffHours >= policy.hoursBeforeArrival ? "Remboursement total si annulation maintenant" : `Remboursement ${100 - policy.penaltyPercentage}%`;
                policyColor = "text-yellow-600";
              }
              if (hasPolicy && policy.type === "non_refundable") {
                policyText   = "Non remboursable";
                refundText   = "Aucun remboursement";
                policyColor  = "text-red-500";
                isRefundable = false;
              }

              const canCancel      = r.status === "confirmed" && isUpcoming && isRefundable;
              const canReview      = r.status === "completed" && !r.review?.submitted;
              const alreadyReviewed = r.status === "completed" && r.review?.submitted;

              // Un avis est modifiable seulement si submitted mais encore pending
              const canEdit = alreadyReviewed && r.review?.reviewId?._id && r.review?.reviewId?.status === "pending";

              const isReviewOpen = openReviewId === r._id;
              const isEditOpen   = editReviewId === r._id;

              return (
                <motion.div
                  key={r._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group bg-white rounded-2xl border overflow-hidden hover:shadow-md transition"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* IMAGE */}
                    <div className="relative md:w-[240px] h-[180px] overflow-hidden shrink-0">
                      <img
                        src={r.roomId?.photos?.[0]}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1 p-5 flex flex-col justify-between">
                      <div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin size={14} />
                          {r.hotelId?.name} · {r.hotelId?.city}
                        </p>
                        <h2 className="text-lg font-semibold text-gray-900 mt-1">
                          {r.roomId?.title}
                        </h2>
                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                          <CalendarDays size={14} />
                          {new Date(r.checkInDate).toLocaleDateString()} →{" "}
                          {new Date(r.checkOutDate).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">Réf : {r.reservationId}</p>

                        {/* Badge séjour */}
                        <div className="mt-2">
                          {isUpcoming && <span className="text-xs text-blue-500">À venir</span>}
                          {isOngoing  && <span className="text-xs text-green-600">En cours</span>}
                          {r.status === "completed" && <span className="text-xs text-gray-400">Terminé</span>}
                        </div>

                        {/* Policy */}
                        <div className="mt-2">
                          <p className={`text-xs ${policyColor}`}>{policyText}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{refundText}</p>
                      </div>

                      {/* BOTTOM */}
                      <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span className={`w-2 h-2 rounded-full ${status.color}`} />
                          {status.text}
                        </span>

                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">{r.totalPrice}€</p>
                          {canCancel && (
                            <button onClick={() => handleCancelRequest(r._id)} className="text-sm mt-1 text-red-500 hover:text-red-600">
                              Demander l'annulation
                            </button>
                          )}
                          {!canCancel && r.status === "confirmed" && (
                            <p className="text-xs text-gray-400">Annulation non disponible</p>
                          )}
                          {r.status === "pending_cancel" && (
                            <p className="text-xs text-yellow-500">⏳ En attente de validation</p>
                          )}
                        </div>
                      </div>

                      {/* ── ZONE AVIS ─────────────────────────────── */}

                      {/* Avis soumis + encore pending → modifiable */}
                      {alreadyReviewed && (
                        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            ✓ Avis soumis
                            {r.review?.reviewId?.status === "pending" && (
                              <span className="ml-1 text-amber-500">— en attente de validation</span>
                            )}
                            {r.review?.reviewId?.status === "approved" && (
                              <span className="ml-1 text-green-500">— publié</span>
                            )}
                          </p>
                          {canEdit && !isEditOpen && (
                            <button
                              onClick={() => setEditReviewId(r._id)}
                              className="text-xs text-[#3A9D23] hover:text-[#2e8019] font-medium underline underline-offset-2 transition"
                            >
                              Modifier
                            </button>
                          )}
                        </div>
                      )}

                      {/* Form modification */}
                      {isEditOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          className="mt-4"
                        >
                          <ReviewForm
                            mode="edit"
                            hotelName={r.hotelId?.name}
                            reviewId={r.review?.reviewId?._id}
                            initialRating={r.review?.reviewId?.rating || 0}
                            initialComment={r.review?.reviewId?.comment || ""}
                            onSuccess={(data) => handleEditSuccess(r._id, data)}
                          />
                          <button
                            onClick={() => setEditReviewId(null)}
                            className="mt-2 text-xs text-gray-400 hover:text-gray-600"
                          >
                            Annuler
                          </button>
                        </motion.div>
                      )}

                      {/* Nouveau avis */}
                      {canReview && (
                        <div className="mt-4">
                          {!isReviewOpen ? (
                            <button
                              onClick={() => setOpenReviewId(r._id)}
                              className="text-sm text-[#3A9D23] hover:text-[#2e8019] font-medium underline underline-offset-2 transition"
                            >
                              ✍️ Laisser un avis
                            </button>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ReviewForm
                                mode="create"
                                reservationId={r._id}
                                hotelName={r.hotelId?.name}
                                onSuccess={() => handleReviewSuccess(r._id)}
                              />
                              <button
                                onClick={() => setOpenReviewId(null)}
                                className="mt-2 text-xs text-gray-400 hover:text-gray-600"
                              >
                                Annuler
                              </button>
                            </motion.div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default MyReservations;